import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { useLocalizedPlaces } from 'hooks/usePlace'
import { getCruiseExtraFilters, getGlobalFacetFilters } from 'lib/cruises/cruiseUtils'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import CruiseAllLocationItem from './CruiseAllLocationsItem'
import CruiseLocationItem from './CruiseLocationItem'
import useQueryParams from 'hooks/useQueryParams'
import { omitKeys } from 'lib/object/objectUtils'
import { useCruiseSearchInput } from '../CruiseSearchInputContext'

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(${rem(288)},1fr));
  column-gap: ${rem(40)};
`

interface Props {
  locations: Array<App.PortFacetV2Item>
  searchTerm: string
  searchItems: Array<App.SearchItem>
  isAllSelected: boolean
  onChange: (values: Array<App.SearchItem>) => void
  onSelectAll: () => void
  inputSearchRef?: React.RefObject<HTMLInputElement | null>
  hideImages?: boolean
  initialFilters?: App.CruiseInitialFilters
}

function CruiseLocationContent(props: Props) {
  const {
    locations,
    searchTerm,
    searchItems,
    isAllSelected,
    onChange,
    onSelectAll,
    inputSearchRef,
    hideImages,
    initialFilters,
  } = props
  const queryParams = useQueryParams()
  const cruiseExtraFilters = getCruiseExtraFilters(queryParams)
  const globalFilters = useContext(GlobalSearchStateContext)
  const facetFilters = getGlobalFacetFilters(globalFilters)

  const { isOpen } = useCruiseSearchInput()
  const [filteredFacetsV2, fetching] = useCruiseSearchFacets({
    facetTypes: ['destination_ports'],
    ...omitKeys(
      ['destinationId', 'destinationIds', 'destinationName', 'destinationNames'],
      { ...(initialFilters || {}), ...cruiseExtraFilters, ...facetFilters },
    ),
  }, { isDisabled: !isOpen })
  const filteredFacets = filteredFacetsV2.destinationPorts

  const placeIds = useMemo(
    () => locations.map(l => l.slug),
    [locations],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const searchLocations = useMemo(() => locations.map((location) => {
    const [shortName, ...info] = location.label.split(', ')

    const localized = localizedPlaces[location.slug]
    const locationName = localized?.place?.name ?? (location.isTrend ? location.label : shortName)
    const locationInfo = localized?.place?.location ?? (location.isTrend ? '' : info.join(', '))

    const searchItem = {
      searchType: 'destination',
      value: location.slug,
      format: {
        mainText: locationName,
        secondaryText: locationInfo,
      },
    } as App.SearchItem

    const isSelected = (
      (isAllSelected && !searchTerm.length) ||
      searchItems.some(({ value }) => value === searchItem.value)
    )

    const isDisabled = !filteredFacets.some(({ slug }) => slug === location.slug)

    return {
      searchItem,
      imageId: location.imageId,
      isTrend: location.isTrend,
      isSelected,
      isDisabled,
    }
  }), [locations, localizedPlaces, isAllSelected, searchItems, searchTerm, filteredFacets])

  useEffect(() => {
    if (inputSearchRef?.current) {
      inputSearchRef.current.focus({ preventScroll: true })
    }
  }, [inputSearchRef])

  const handlerSelectItem = useCallback((item: App.SearchItem, isRemoving?: boolean) => {
    if (isAllSelected && isRemoving) {
      // When all items are selected and one is removed, it should add all trend destinations except the removed one
      const newSearchItems = searchLocations
        .filter(({ isTrend, searchItem }) => isTrend && searchItem.value !== item.value)
        .map(({ searchItem }) => searchItem)

      onChange(newSearchItems)
      return
    }

    const newSearchItems = isRemoving ? searchItems.filter(({ value }) => value !== item.value) : [...searchItems, item]
    onChange(newSearchItems)
  }, [searchItems, searchLocations, isAllSelected, onChange])

  return <VerticalSpacer gap={16}>
    {fetching && <>
      <ListItemLoadingSkeleton subtitle />
      <ListItemLoadingSkeleton subtitle />
      <ListItemLoadingSkeleton subtitle />
    </>}

    {!fetching && <LocationGrid>
      {!searchTerm && <CruiseAllLocationItem
        isSelected={isAllSelected}
        onSelectAll={onSelectAll}
        hideImages={hideImages}
      />}

      {searchLocations.map(({ searchItem, imageId, isSelected, isDisabled }) => <CruiseLocationItem
        key={searchItem.value}
        imageId={imageId}
        searchItem={searchItem}
        onChange={handlerSelectItem}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isSearching={!!searchTerm}
        searchTerm={searchTerm}
        hideImages={hideImages}
      />)}
    </LocationGrid>}
  </VerticalSpacer>
}

export default CruiseLocationContent
